import { ref } from "vue";
import {
  activateLinguaNamespace,
  APP_NAMESPACE,
  BDE_NAMESPACE,
  IOT_NAMESPACE,
  changeLinguaLanguage,
  createLinguaRuntime,
  getLinguaMessage,
  getLinguaOccurrence,
  getLinguaTranslationsByPrefix,
  hasLinguaTranslation,
  linguaRevision,
  translateLingua,
} from "@/lingua";

export const staticTranslationRevision = linguaRevision;
export const createStaticTranslationRuntime = createLinguaRuntime;
export const changeStaticTranslationLanguage = changeLinguaLanguage;

export function translateStaticApp(key: string, ...arguments_: unknown[]): string {
  return translateLingua(APP_NAMESPACE, key, ...arguments_);
}

export function hasStaticAppTranslation(key: string, _language?: string): boolean {
  return hasLinguaTranslation(APP_NAMESPACE, key);
}

export function getStaticAppTranslationsByPrefix(
  prefix: string,
  _language?: string,
): Record<string, string> {
  return getLinguaTranslationsByPrefix(APP_NAMESPACE, prefix);
}

export const staticMessage = getLinguaMessage;
export const translateStaticMessage = translateLingua;
export const staticOccurrence = getLinguaOccurrence;

function useNamespaceTranslate(namespace: string) {
  const isLoading = ref(namespace !== APP_NAMESPACE);
  if (namespace !== APP_NAMESPACE) {
    void activateLinguaNamespace(namespace)
      .catch((error) => {
        // The namespace loader also attempts the bundled fallback.
        // eslint-disable-next-line no-console
        console.error(`Failed to activate Lingua namespace ${namespace}`, error);
      })
      .finally(() => {
        isLoading.value = false;
      });
  }
  return {
    t: (key: string, ...arguments_: unknown[]) => {
      void linguaRevision.value;
      // A lazy namespace has no catalog during its first render. Wait for its
      // activation before asking Lingua to diagnose genuinely missing keys.
      if (isLoading.value && !hasLinguaTranslation(namespace, key)) return "";
      return translateLingua(namespace, key, ...arguments_);
    },
    isLoading,
  };
}

export function useAppTranslate() {
  return useNamespaceTranslate(APP_NAMESPACE);
}

export function useBdeTranslate() {
  return useNamespaceTranslate(BDE_NAMESPACE);
}

export function useIoTTranslate() {
  return useNamespaceTranslate(IOT_NAMESPACE);
}

export type AppTranslate = ReturnType<typeof useAppTranslate>["t"];
export type BdeTranslate = ReturnType<typeof useBdeTranslate>["t"];
export type IoTTranslate = ReturnType<typeof useIoTTranslate>["t"];
